import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import InfoTip from '../../components/novarium/InfoTip';
import { ServiceStatisticsBenchmarkDTO, ServiceStatisticsMetricsDTO } from '../../dto/ServiceStatisticsDTO';
import {
    BenchmarkIndicator, BenchmarkRow, BenchmarkTrend, benchmarkDifference, buildBenchmarkRows, formatMinutes,
} from './statisticsUtils';

interface SpecialityBenchmarkProps {
    /** Null si todavía no hay (o el backend no lo mandó); se trata como "sin datos suficientes". */
    benchmark: ServiceStatisticsBenchmarkDTO | null;
    /** La consulta falló: se avisa en la card y el resto de la pantalla sigue andando. */
    failed: boolean;
    /** Indicadores del profesional con los filtros aplicados. */
    current: ServiceStatisticsMetricsDTO;
}

const INDICATOR_LABEL_ID: Record<BenchmarkIndicator, string> = {
    wait: 'ServicesStatisticsKpiWait',
    waitP90: 'ServicesStatisticsKpiWaitP90',
    service: 'ServicesStatisticsKpiService',
};

const TREND_ICON: Record<BenchmarkTrend, string> = {
    more: 'pi-arrow-up',
    less: 'pi-arrow-down',
    same: 'pi-minus',
};

const TREND_MESSAGE_ID: Record<BenchmarkTrend, string> = {
    more: 'ProfessionalPerformanceBenchMore',
    less: 'ProfessionalPerformanceBenchLess',
    same: 'ProfessionalPerformanceBenchSame',
};

const EMPTY = '—';

function formatValue(value: number | null): string {
    return value === null ? EMPTY : formatMinutes(value);
}

/** "8,0 min menos que el promedio": la dirección va en la flecha y en el texto, nunca solo en un color. */
function DifferenceCell({ intl, row }: { intl: IntlShape; row: BenchmarkRow }) {
    const difference = benchmarkDifference(row);
    if (!difference) return <>{EMPTY}</>;
    const amount = formatMinutes(difference.amount);
    return (
        <span className="ss-bench-diff">
            <i className={`pi ${TREND_ICON[difference.trend]}`} aria-hidden="true" />
            <span>{intl.formatMessage({ id: TREND_MESSAGE_ID[difference.trend] }, { value: amount })}</span>
        </span>
    );
}

/**
 * «Vos y tu especialidad»: espera media, percentil 90 y duración media del profesional contra el promedio
 * de todas las atenciones de su especialidad en el período. Es anónimo: nunca hay nombres ni valores de colegas, y con
 * menos de 3 profesionales el backend no manda cifras (`available = false`). Las diferencias no se juzgan con color: no
 * se sabe si una duración mayor es mejor o peor, solo cuánto se aparta del promedio. La especialidad va en la cabecera del
 * promedio, y el ⓘ de esa cabecera dice con cuántos profesionales se calculó y qué filtros lleva cada columna.
 */
export default function SpecialityBenchmark({ benchmark, failed, current }: SpecialityBenchmarkProps) {
    const intl = useIntl();

    const rows = useMemo(() => (benchmark?.available && benchmark.metrics
        ? buildBenchmarkRows(current, benchmark.metrics)
        : []), [benchmark, current]);

    const speciality = benchmark?.specialityName ?? '';
    const compared = !failed && rows.length > 0;

    const mineBody = (row: BenchmarkRow) => formatValue(row.mine);
    const averageBody = (row: BenchmarkRow) => formatValue(row.average);
    const differenceBody = (row: BenchmarkRow) => <DifferenceCell intl={intl} row={row} />;
    const indicatorBody = (row: BenchmarkRow) => intl.formatMessage({ id: INDICATOR_LABEL_ID[row.id] });

    return (
        <section className="nv-card ss-bench" aria-labelledby="ss-bench-title">
            <div className="ss-card-head">
                <h2 className="nv-section-title" id="ss-bench-title">
                    {intl.formatMessage({ id: 'ProfessionalPerformanceBenchTitle' })}
                </h2>
            </div>

            {failed && (
                <p className="nv-muted ss-empty">{intl.formatMessage({ id: 'ProfessionalPerformanceBenchFailed' })}</p>
            )}
            {!failed && !compared && (
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'ProfessionalPerformanceBenchUnavailable' })}
                </p>
            )}
            {compared && (
                <>
                    <DataTable
                        className="ss-table ss-bench-table"
                        value={rows}
                        dataKey="id"
                        aria-label={intl.formatMessage({ id: 'ProfessionalPerformanceBenchLabel' })}
                    >
                        <Column header={intl.formatMessage({ id: 'ProfessionalPerformanceBenchColIndicator' })} body={indicatorBody} />
                        <Column header={intl.formatMessage({ id: 'ProfessionalPerformanceBenchColMine' })}
                            body={mineBody} className="ss-center" headerClassName="ss-center" />
                        <Column
                            header={<>
                                {intl.formatMessage({ id: 'ProfessionalPerformanceBenchColAverage' }, { speciality })}
                                <InfoTip text={intl.formatMessage({ id: 'ProfessionalPerformanceBenchHelp' },
                                    { count: benchmark?.professionals ?? 0 })} />
                            </>}
                            body={averageBody} className="ss-center" headerClassName="ss-center" />
                        <Column header={intl.formatMessage({ id: 'ProfessionalPerformanceBenchColDifference' })}
                            body={differenceBody} />
                    </DataTable>
                </>
            )}
        </section>
    );
}
